import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram } from '../gpu/shader';
import { BaseEffect } from './base';
import { VERTEX_FULLSCREEN, FRAG_LENS_FLARE } from '../shaders/glsl';

const DEFAULTS = {
  intensity: 0.75,
  scale: 1.15,
  positionX: 0.72,
  positionY: 0.28,
  streakLength: 1.4,
};

let flareProg: WebGLShaderProgram | null = null;

export class LensFlareEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'lens_flare', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!flareProg) flareProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_LENS_FLARE);
    return this.runShaderPass(frame, flareProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_intensity', params.intensity);
      prog.setUniform1f('u_scale', params.scale);
      prog.setUniform2f('u_position', params.positionX, params.positionY);
      prog.setUniform1f('u_streakLength', params.streakLength);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, _context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    const cx = params.positionX * frame.width;
    const cy = params.positionY * frame.height;
    const s = params.scale;
    const inten = params.intensity;

    ctx.globalCompositeOperation = 'lighter';

    // Primary halo
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, 160 * s);
    halo.addColorStop(0, `rgba(255,245,220,${0.55 * inten})`);
    halo.addColorStop(0.25, `rgba(255,210,140,${0.28 * inten})`);
    halo.addColorStop(0.6, `rgba(255,160,80,${0.1 * inten})`);
    halo.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, frame.width, frame.height);

    // Anamorphic streak
    const streakW = frame.width * 0.55 * params.streakLength;
    const streak = ctx.createLinearGradient(cx - streakW, cy, cx + streakW, cy);
    streak.addColorStop(0, 'rgba(255,220,180,0)');
    streak.addColorStop(0.45, `rgba(255,235,200,${0.35 * inten})`);
    streak.addColorStop(0.5, `rgba(255,250,240,${0.7 * inten})`);
    streak.addColorStop(0.55, `rgba(180,210,255,${0.35 * inten})`);
    streak.addColorStop(1, 'rgba(140,180,255,0)');
    ctx.fillStyle = streak;
    ctx.fillRect(cx - streakW, cy - 2.5 * s, streakW * 2, 5 * s);

    // Ghost orbs
    const ghosts = [
      { t: 0.3, r: 18 * s, c: [255, 180, 100] },
      { t: 0.5, r: 28 * s, c: [120, 190, 255] },
      { t: 0.7, r: 14 * s, c: [255, 120, 200] },
      { t: 0.85, r: 22 * s, c: [255, 220, 140] },
    ];
    for (const g of ghosts) {
      const gx = cx + (frame.width * 0.5 - cx) * g.t * 2;
      const gy = cy + (frame.height * 0.5 - cy) * g.t * 2;
      const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, g.r);
      grad.addColorStop(0, `rgba(${g.c[0]},${g.c[1]},${g.c[2]},${0.45 * inten})`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(gx, gy, g.r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = 'source-over';
    return { width: frame.width, height: frame.height, canvas, texture: null };
  }
}
